import { useTranslation } from 'react-i18next'
import { Dialog } from 'primereact/dialog'
import { InputTextarea } from 'primereact/inputtextarea'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import Button from '../../../components/ui/Button'
import { formatDate, formatDateTime, formatTime } from '../../../lib/datetime'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../../lib/useMediaQuery'
import type { ActionNote } from '../types'

/*
 * Görev Notu yüzeyleri — bölüm + tablo + düzenleme popup'ı.
 *
 * ActionDetailPage'den 2026-08-28'de ÇIKARILDI: alt görev detayına da not alanı eklendi (kullanıcı
 * isteği "tıpkı ana görev gibi") ve iki sayfa aynı yüzeyi paylaşıyor. Kopyalansaydı biri değişip
 * diğeri kalırdı — nitekim not kurallarının (termin değişince zorunlu, ACTION_NOTE_MANAGE ile
 * düzenle/sil) iki ekranda ayrışmaması işin ta kendisi. Emsal: ActionDocumentsSection.
 *
 * Bileşenler DURUM TUTMAZ: metin, popup açıklığı ve mutation'lar çağıran sayfada durur — iki sayfanın
 * kaydetme akışı farklıdır (ana görevde alan+not TEK işlem, alt görevde de aynı ama farklı uç).
 */
/**
 * Aksiyon Notu bölümü (7.8) — yalnız metin; kronolojik, değiştirilemez geçmiş. Not girişi popup'ta; Kaydet
 * alan/hedef teslim tarihi + notu TEK işlemde yazar (7.10). **Alt görevde de render edilir**
 * (2026-08-28: 066/AK-7'nin "alt görevde not yoktur" kuralı kalktı) — bileşen tam olarak iki sayfada
 * paylaşılsın diye sayfa JSX'inden ÇIKARILDI (ayrıca Sonar S3776).
 */
export function ActionNotesSection({
  notes,
  editable,
  canManageNotes,
  note,
  noteDialogOpen,
  deadlineChanged,
  pending,
  onNote,
  onNoteDialog,
  onSubmitNote,
  onSaveFields,
  onEditNote,
  onDeleteNote,
  saveLabel = 'actions.detail.update.save',
}: Readonly<{
  notes: ActionNote[]
  editable: boolean
  canManageNotes: boolean
  note: string
  noteDialogOpen: boolean
  deadlineChanged: boolean
  pending: boolean
  onNote: (v: string) => void
  onNoteDialog: (open: boolean) => void
  onSubmitNote: () => void
  onSaveFields: () => void
  onEditNote: (n: ActionNote) => void
  onDeleteNote: (n: ActionNote) => void
  /**
   * Alttaki kaydet butonunun i18n anahtari. Ana gorevde "Gorev Guncelle", alt gorevde "Kaydet" yazar —
   * iki sayfanin kendi sozlugu var ve buton alan+not kaydini birlikte yaptigi icin bolumun ICINDE durur.
   */
  saveLabel?: string
}>) {
  const { t } = useTranslation()
  return (
    <div className="minutes-subsection act-notes-sub">
      {/* Not girişi POPUP'ta (Not Ekle) — notlar tablosunun ÜSTÜNDE. Aksiyon Güncelle ise EN ALTA. */}
      {editable && (
        <div className="act-desc-actions">
          <Button
            variant="secondary"
            icon="pi pi-plus"
            label={t('actions.detail.notes.add')}
            onClick={() => onNoteDialog(true)}
          />
        </div>
      )}

      {/* Aksiyon Notu — popup: yalnız metin. Kaydet → alan/termin + not TEK işlemde (7.10). */}
      <Dialog
        visible={noteDialogOpen}
        header={t('actions.detail.notes.add')}
        modal
        dismissableMask
        draggable={false}
        style={{ width: '38rem', maxWidth: '95vw' }}
        onHide={() => onNoteDialog(false)}
        footer={
          <div className="ui-dialog-footer">
            <Button
              icon="pi pi-check"
              label={t('actions.detail.notes.save')}
              onClick={onSubmitNote}
              disabled={pending || note.trim() === ''}
            />
          </div>
        }
      >
        <div className="act-field act-field--full act-note-input">
          <InputTextarea
            value={note}
            rows={4}
            autoResize
            maxLength={4000}
            placeholder={t('actions.detail.notes.fieldPlaceholder')}
            className={deadlineChanged && note.trim() === '' ? 'p-invalid' : undefined}
            onChange={(e) => onNote(e.target.value)}
          />
          {deadlineChanged && (
            <span className="act-note-savehint">{t('actions.detail.notes.deadlineHint')}</span>
          )}
        </div>
      </Dialog>

      <ActionNotesTable
        notes={notes}
        canManageNotes={canManageNotes}
        onEdit={onEditNote}
        onDelete={onDeleteNote}
      />

      {/* Aksiyon Güncelle (7.10) — bu alanın EN ALTINDA; alan/termin + not/dosya TEK işlemde. */}
      {editable && (
        <div className="act-desc-actions act-box-actions">
          <Button
            icon="pi pi-check"
            label={t(saveLabel)}
            onClick={onSaveFields}
            disabled={pending}
          />
        </div>
      )}
    </div>
  )
}

/** Notlar tablosu (Ekleyen · Tarih · Saat · Not [· işlemler]); not yoksa boş-durum metni. İşlem kolonu yalnız
 *  ACTION_NOTE_MANAGE yetkisinde (herhangi bir notu Düzenle/Sil).
 *
 *  <p>Dar ekranda (≤`CARD_VIEW_BREAKPOINT`) tablo yerine kart listesi çizilir — uygulamanın geri
 *  kalanıyla aynı desen (bkz. `MOBILE_TABLET_RESPONSIVE_OVERHAUL.md`). O tur **liste sayfalarını**
 *  kapsamış, detay içindeki tabloları kapsamamıştı; dört kolon tablet genişliğinde bile okunmuyordu
 *  (kullanıcı bildirimi 2026-09-09). Masaüstü dalı **hiç değişmedi**. */
export function ActionNotesTable({
  notes,
  canManageNotes,
  onEdit,
  onDelete,
}: Readonly<{
  notes: ActionNote[]
  canManageNotes: boolean
  onEdit: (n: ActionNote) => void
  onDelete: (n: ActionNote) => void
}>) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)
  if (notes.length === 0) {
    return <p className="act-empty-hint act-notes-empty">{t('actions.detail.notes.empty')}</p>
  }
  if (isMobile) {
    return (
      <ActionNoteCards
        notes={notes}
        canManageNotes={canManageNotes}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    )
  }
  return (
    <DataTable value={notes} dataKey="id" paginator={false} className="minutes-participant-table">
      <Column
        header={t('actions.detail.notes.colAuthor')}
        headerStyle={{ width: '14rem' }}
        body={(n: ActionNote) => n.authorName ?? '—'}
      />
      <Column
        header={t('actions.detail.notes.colDate')}
        headerStyle={{ width: '10rem' }}
        body={(n: ActionNote) => formatDate(n.creDate)}
      />
      <Column
        header={t('actions.detail.notes.colTime')}
        headerStyle={{ width: '8rem' }}
        body={(n: ActionNote) => formatTime(n.creDate)}
      />
      <Column
        header={t('actions.detail.notes.colNote')}
        body={(n: ActionNote) => (
          <span className="act-note-text" title={n.note}>
            {n.note}
          </span>
        )}
      />
      {canManageNotes && (
        <Column
          header=""
          headerStyle={{ width: '6rem' }}
          bodyStyle={{ textAlign: 'center' }}
          body={(n: ActionNote) => (
            <div className="act-note-actions">
              <button
                type="button"
                className="minutes-remove-btn"
                aria-label={t('actions.detail.notes.edit')}
                title={t('actions.detail.notes.edit')}
                onClick={() => onEdit(n)}
              >
                <i className="pi pi-pencil" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="minutes-remove-btn"
                aria-label={t('actions.detail.notes.delete')}
                title={t('actions.detail.notes.delete')}
                onClick={() => onDelete(n)}
              >
                <i className="pi pi-trash" aria-hidden="true" />
              </button>
            </div>
          )}
        />
      )}
    </DataTable>
  )
}

/**
 * Notların dar ekran karşılığı: her not bir kart (Ekleyen · zaman · not metni [· işlemler]).
 *
 * <p>Masaüstündeki dört kolon karta şöyle iniyor: **Ekleyen** başlık, **Tarih + Saat** tek bir zaman
 * damgasına birleşiyor (`formatDateTime` — kartta iki ayrı alan olarak durmaları yer kaybıydı, kolon
 * başlığı da yok), **Not** gövde. İşlem butonları masaüstündeki `minutes-remove-btn` işaretlemesini
 * aynen kullanır, böylece iki dal görsel olarak ayrışmaz.
 *
 * <p>Kart TIKLANABİLİR DEĞİL: notun detay sayfası yok, satır tıklaması masaüstünde de bir şey yapmıyor
 * — bu yüzden `ui-card--clickable`/`role="button"` bilinçle konmadı (bkz. ui.css'teki not: salt-liste
 * ekranlarında kart tıklanmaz).
 */
function ActionNoteCards({
  notes,
  canManageNotes,
  onEdit,
  onDelete,
}: Readonly<{
  notes: ActionNote[]
  canManageNotes: boolean
  onEdit: (n: ActionNote) => void
  onDelete: (n: ActionNote) => void
}>) {
  const { t } = useTranslation()
  return (
    <div className="ui-card-list act-note-cards">
      {notes.map((n) => (
        <div key={n.id} className="ui-card">
          <div className="ui-card-head">
            <span className="ui-card-title">{n.authorName ?? '—'}</span>
            <span className="ui-card-sub">{formatDateTime(n.creDate)}</span>
          </div>
          <div className="ui-card-row">
            <span className="act-note-text">{n.note}</span>
          </div>
          {canManageNotes && (
            <div className="ui-card-actions">
              <button
                type="button"
                className="minutes-remove-btn"
                aria-label={t('actions.detail.notes.edit')}
                title={t('actions.detail.notes.edit')}
                onClick={() => onEdit(n)}
              >
                <i className="pi pi-pencil" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="minutes-remove-btn"
                aria-label={t('actions.detail.notes.delete')}
                title={t('actions.detail.notes.delete')}
                onClick={() => onDelete(n)}
              >
                <i className="pi pi-trash" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

/** Aksiyon notu düzenleme popup'ı (yalnız metin) — ACTION_NOTE_MANAGE. Ekleyen/zaman korunur; optimistic lock. */
export function NoteEditDialog({
  open,
  text,
  pending,
  onText,
  onSubmit,
  onClose,
}: Readonly<{
  open: boolean
  text: string
  pending: boolean
  onText: (v: string) => void
  onSubmit: () => void
  onClose: () => void
}>) {
  const { t } = useTranslation()
  return (
    <Dialog
      visible={open}
      header={t('actions.detail.notes.editTitle')}
      modal
      dismissableMask
      draggable={false}
      style={{ width: '38rem', maxWidth: '95vw' }}
      onHide={onClose}
      footer={
        <div className="ui-dialog-footer">
          <Button
            icon="pi pi-check"
            label={t('actions.detail.notes.save')}
            onClick={onSubmit}
            loading={pending}
            disabled={text.trim() === ''}
          />
        </div>
      }
    >
      <div className="act-field act-field--full act-note-input">
        <InputTextarea
          value={text}
          rows={4}
          autoResize
          maxLength={4000}
          placeholder={t('actions.detail.notes.fieldPlaceholder')}
          onChange={(e) => onText(e.target.value)}
        />
      </div>
    </Dialog>
  )
}
